/// <amd-module name="Build/Run/Components/StageGraph" />
import * as React from "react";

import { TaskResult, TimelineRecordState } from "Build/Client/Contracts/Build";
import { CheckReference, RunDetailsData, RunJobData, RunStageData, StageStateData } from "Build/Client/Contracts/DataProvider";
import { CheckRunStatus } from "Build/Client/Contracts/PipelinesChecks";
import { IBuildLinkingService } from "Build/Client/Linking";
import { IConfirmPipelineActionProps } from "Build/Common/Commands/ConfirmPipelineAction/Command.types";
import { CommandComponent } from "Build/Common/Library/Components/CommandComponent";
import { NavConstants } from "Build/Common/Library/Constants";
import { getTimelineStatusIndicatorData, IStatusIndicatorData } from "Build/Common/Library/Indicator";
import * as Resources from "Build/Run/Resources";

import { ArtifactStatus } from "Build/Run/Components/ArtifactStatus";
import { GraphList } from "Build/Run/Components/Canvas/Graph";
import { INodeData } from "Build/Run/Components/Canvas/Types";
import { TestStatus } from "Build/Run/Components/TestStatus";
import { ApprovalCheckTypeId, ManualValidationTaskTypeIdString } from "Build/Run/Constants";
import { IRunDetailsService } from "Build/Run/RunDetailsService.types";

import { IReadonlyObservableValue } from "VSS/Core/Observable";
import { equals, format } from "VSS/Core/Util/String";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FPSLink } from "VSS/Features/PlatformUI/FPSLink";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, IVssComponentProperties, IVssLayoutManager, VssComponent } from "VSS/Platform/Layout";

import { Button, IButtonProps } from "VSSUI/Button";
import { Duration } from "VSSUI/Duration";
import { FocusGroupContext } from "VSSUI/FocusGroup";
import { FocusZone, FocusZoneContext, FocusZoneDirection } from "VSSUI/FocusZone";
import { Status, StatusSize } from "VSSUI/Status";
import { Tooltip } from "VSSUI/TooltipEx";
import { css, KeyCode } from "VSSUI/Util";
import * as DateUtil from "VSSUI/Utilities/Date";
import { CheckStatus, ManualValidationStatus } from "./CheckStatus";

import { ISplitButtonProps, SplitButton } from "VSSUI/SplitButton";
import { IMenuItem } from "VSSUI/Menu";

export interface IStageGraphProps extends IVssComponentProperties {
    onReviewStageClicked: (stage: RunStageData) => void;
    onReviewManualValidationClicked: (stage: RunStageData) => void;
}

export interface IStageGraphState {
    run: RunDetailsData;
    expanded: string[];
    minimized: string[];
    cancelledStages: string[];
    retriedStages: string[];
}

const PrimaryMessage = (props: { text: string }) => {
    return <span className="primary-message">
        <span className="message">
            {props.text}
        </span>
    </span>;
}

const primaryCardHeight = 72;
const Metadataheight = 28;
const stageActionButtonHeight = 58;
const secondaryCardPadding = 4;
const jobRowHeight = 24;
const jobRowMargin = 8;

const enum StageDependencyState {
    None = 0,
    InProgress = 1,
    Completed = 2
}

interface IStageMetadata {
    [key: string]: {
        hasTests: boolean;
        hasArtifacts: boolean;
        hasChecks: boolean;
    }
}

export class StageGraph extends VssComponent<IStageGraphProps, IStageGraphState> {
    private _service: IRunDetailsService;
    private _observable: IReadonlyObservableValue<RunDetailsData>;
    private _expanded: string[];
    private _minimized: string[];
    private _cancelledStages: string[] = [];
    private _retriedStages: string[] = [];
    private _stageMetadata: IStageMetadata = {};
    private _showCheckIssues: Boolean;
    private _retryStageWithoutDependencies: Boolean;

    constructor(props: IStageGraphProps, context: IVssComponentContext) {
        super(props, context);

        this._service = this.context.pageContext.getService<IRunDetailsService>("IRunDetailsService");
        this._observable = this._service.data;

        this._expanded = [];
        this._minimized = [];

        this._showCheckIssues = isFeatureFlagEnabled(context.pageContext, "Pipelines.Checks.EnableChecksScalabilityPhase1BuildCheckIssues", false)
        this._retryStageWithoutDependencies = isFeatureFlagEnabled(context.pageContext, "Pipelines.RetryWithOrWithoutDependencies", false)

        this.state = {
            run: this._service.data.value,
            expanded: this._expanded,
            minimized: this._minimized,
            cancelledStages: this._cancelledStages,
            retriedStages: this._retriedStages
        };
    }

    public render() {
        // The Graph component requires provided heights, so these values need to be
        // kept up to date with changes to the css styles of the stage cards to ensure
        // layout of the graph nodes and edges is correct.
        const stages = this.state.run.stages;
        const jobs = this.state.run.jobs;

        const nodes: INodeData[] = [];
        const edges: { [key: string]: { [key: string]: boolean } } = {};

        const linkingService = this.context.pageContext.getService<IBuildLinkingService>("IBuildLinkingService");

        stages.forEach((stage: RunStageData) => {
            const jobIds: string[] = [];
            let completedJobCount = 0;

            const jobRows: JSX.Element[] = [];

            const onReviewApprovalsClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                if (event) {
                    event.stopPropagation();
                    event.preventDefault();
                }
                this.props.onReviewStageClicked(stage);
            }

            const onReviewManualValidationClick = (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                if (event) {
                    event.stopPropagation();
                    event.preventDefault();
                }
                this.props.onReviewManualValidationClicked(stage);
            }

            jobs.forEach((job: RunJobData) => {
                if (job.stageId === stage.id) {
                    if (job.state === TimelineRecordState.Completed) {
                        completedJobCount++;
                    }
                    jobIds.push(job.id);

                    const jobRoutes: { [key: string]: string } = {};
                    jobRoutes[NavConstants.stage] = stage.id;
                    jobRoutes[NavConstants.job] = job.id;
                    const jobUrl = linkingService.getBuildUrl(this.state.run.id, NavConstants.logs, undefined, undefined, jobRoutes);

                    jobRows.push(<JobRow job={job} jobUrl={jobUrl} key={job.id} status={stage.stateData} />);
                }
            });

            const cardStatus = this._getStageCardStatus(this.state.run.id, stage.refName, stage.state, stage.result, jobIds, completedJobCount, stage.stateData, stage.checks, this.state.cancelledStages, onReviewApprovalsClick, onReviewManualValidationClick);
            const totalJobCount = jobIds.length;
            const stageStatusMessage = cardStatus.message;
            let showExpandedCard = cardStatus.expanded;
            const status = cardStatus.status;
            const failedChecks = stage.checks ? stage.checks.filter(check => check.status === CheckRunStatus.Failed || check.status === CheckRunStatus.Rejected).length: 0;

            if (this.state.expanded.indexOf(stage.id) >= 0 && totalJobCount > 0) {
                showExpandedCard = true;
            }
            else if (this.state.minimized.indexOf(stage.id) >= 0 || totalJobCount == 0) {
                showExpandedCard = false;
            }
            const stageRoutes: { [key: string]: string } = {};
            stageRoutes[NavConstants.stage] = stage.id;
            const stageUrl = linkingService.getBuildUrl(this.state.run.id, NavConstants.logs, undefined, undefined, stageRoutes);

            const metadataCount = this._getMetadataCount(stage.refName);
            let currentPrimaryCardHeight = primaryCardHeight + (metadataCount * Metadataheight);
            // add cancel button for in progress stages or retry button for stages that completed but were not successful
            let footerButtonProps: IButtonProps[] | undefined;
            let footerSplitButtonProps: ISplitButtonProps | undefined;
            if (stage.state === TimelineRecordState.InProgress) {
                const canceled = this._cancelledStages.indexOf(stage.refName) >= 0;
                footerButtonProps = [{ text: Resources.Cancel, disabled: canceled, onClick: (event: React.MouseEvent<HTMLElement>) => { this._cancelStage(event, stage.refName) }, className: "update-stage" } as IButtonProps];
            } else if (this._retryStageWithoutDependencies && stage.state === TimelineRecordState.Completed) {
                const retried = this._retriedStages.indexOf(stage.refName) >= 0;
                const stageDependencyState = this._getStageDependencyState(stage, stages);
                const hasDependencies = stageDependencyState !== StageDependencyState.None;
                if (stageDependencyState !== StageDependencyState.InProgress) {
                    if ((stage.result === TaskResult.Succeeded || stage.result === TaskResult.SucceededWithIssues) && showExpandedCard) {
                        if(!hasDependencies) {
                            footerButtonProps = [{ text: Resources.RerunSucceeded, disabled: retried, onClick: (event: React.MouseEvent<HTMLElement>) => { this._retryStage(event, stage.refName, Resources.RerunStage, format(Resources.ConfirmRerunStage, stage.refName)) }, className: "update-stage" } as IButtonProps];
                        } else {
                            footerSplitButtonProps = {
                                buttonProps: {
                                    text: Resources.RerunSucceededWithoutDependents,
                                    disabled: retried,
                                    className:"update-stage-split",
                                    onClick: (event: React.MouseEvent<HTMLElement>) => {
                                        this._retryStage(event, stage.refName, Resources.RerunStage, format(Resources.ConfirmRerunStage, stage.refName), false, false)
                                    }
                                } as IButtonProps,
                                menuButtonProps: {
                                    contextualMenuProps: {
                                        menuProps: {
                                            id: "retry-stage-menu",
                                            items: [{
                                                id: "update-stage-button",
                                                text: Resources.RerunSucceededWithDependents
                                            }],
                                            onActivate: (menuItem: IMenuItem, event: React.MouseEvent<HTMLElement>) => {
                                                this._retryStage(event, stage.refName, Resources.RerunStage, format(Resources.ConfirmRerunStageAndDependents, stage.refName))
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    } 
                    else if (stage.result === TaskResult.Failed && failedChecks > 0) {
                        const confirmationText = hasDependencies ? format(Resources.ConfirmRetryStageAndDependents, stage.refName) : format(Resources.ConfirmRetryStage, stage.refName);
                        footerButtonProps = [{ text: Resources.RetryStage, disabled: retried, onClick: (event: React.MouseEvent<HTMLElement>) => { this._retryStage(event, stage.refName, Resources.RetryStage, confirmationText, false, hasDependencies) }, className: "update-stage" } as IButtonProps];
                    } else if (stage.result === TaskResult.Failed || stage.result === TaskResult.Canceled) {
                        const confirmationText = hasDependencies ? format(Resources.ConfirmRerunStageAndDependents, stage.refName) : format(Resources.ConfirmRerunStage, stage.refName);
                        footerButtonProps = [
                            { text: Resources.RerunFromFailed, disabled: retried, onClick: (event: React.MouseEvent<HTMLElement>) => { this._retryStage(event, stage.refName, Resources.RerunStage, format(Resources.ConfirmRerunStageFailedJobs, stage.refName)) }, className: "update-stage" } as IButtonProps,
                            { text: Resources.RerunAll, disabled: retried, onClick: (event: React.MouseEvent<HTMLElement>) => { this._retryStage(event, stage.refName, Resources.RerunStage, confirmationText, true) }, className: "update-stage-secondary" } as IButtonProps
                        ];
                    } else if (stage.result === TaskResult.Skipped && this._showRetryStageButton(stage.checks)) {
                        const confirmationText = hasDependencies ? format(Resources.ConfirmRetryStageAndDependents, stage.refName) : format(Resources.ConfirmRetryStage, stage.refName);
                        footerButtonProps = [{ text: Resources.RetryStage, disabled: retried, onClick: (event: React.MouseEvent<HTMLElement>) => { this._retryStage(event, stage.refName, Resources.RetryStage, confirmationText, false, hasDependencies) }, className: "update-stage" } as IButtonProps];
                    }
                }
            } else if (!this._retryStageWithoutDependencies && this._canRetry(stage, stages)) {
                const retried = this._retriedStages.indexOf(stage.refName) >= 0;
                if ((stage.result === TaskResult.Succeeded || stage.result === TaskResult.SucceededWithIssues) && showExpandedCard) {
                    footerButtonProps = [{ text: Resources.RerunSucceeded, disabled: retried, onClick: (event: React.MouseEvent<HTMLElement>) => { this._retryStageOld(event, stage.refName) }, className: "update-stage" } as IButtonProps];
                } else if (stage.result === TaskResult.Failed && failedChecks > 0) {
                    footerButtonProps = [{ text: Resources.RerunStage, disabled: retried, onClick: (event: React.MouseEvent<HTMLElement>) => { this._retryStageOld(event, stage.refName) }, className: "update-stage" } as IButtonProps];
                } else if (stage.result === TaskResult.Failed || stage.result === TaskResult.Canceled) {
                    footerButtonProps = [
                        { text: Resources.RerunFromFailed, disabled: retried, onClick: (event: React.MouseEvent<HTMLElement>) => { this._retryStageOld(event, stage.refName, RetryType.FailedJobs) }, className: "update-stage" } as IButtonProps,
                        { text: Resources.RerunAll, disabled: retried, onClick: (event: React.MouseEvent<HTMLElement>) => { this._retryStageOld(event, stage.refName, RetryType.AllJobs) }, className: "update-stage-secondary" } as IButtonProps
                    ];
                } else if (stage.result === TaskResult.Skipped && this._showRetryStageButton(stage.checks)) {
                    footerButtonProps = [{ text: Resources.RerunStage, disabled: retried, onClick: (event: React.MouseEvent<HTMLElement>) => { this._retryStageOld(event, stage.refName) }, className: "update-stage" } as IButtonProps];
                }
            }

            if (footerButtonProps !== undefined) {
                currentPrimaryCardHeight += stageActionButtonHeight * footerButtonProps.length;
            }

            if (this._retryStageWithoutDependencies && footerSplitButtonProps !== undefined) {
                currentPrimaryCardHeight += stageActionButtonHeight;
            }

            nodes.push({
                data: stage,
                key: stage.id,
                nodeHeightHint: showExpandedCard ? currentPrimaryCardHeight + secondaryCardPadding + (jobRowHeight + jobRowMargin) * jobRows.length + jobRowMargin : currentPrimaryCardHeight,
                getNodeElement: () => {
                    let stageName = stage.name;
                    if (stageName === undefined || stageName.length === 0) {
                        stageName = Resources.UnnamedStage;
                    }
                    const primaryContent = (
                        <>
                            <div className="flex-row stage-title-row flex-center">
                                <Tooltip text={status.label}>
                                    <span className="flex-row">
                                        <Status {...status.statusProps} className="stage-icon-margin" size={StatusSize.m} />
                                    </span>
                                </Tooltip>
                                <div className="flex-row scroll-hidden flex-grow body-m stage-title">
                                    <Tooltip overflowOnly={true} text={stageName}>
                                        <span className="text-ellipsis">{stageName}</span>
                                    </Tooltip>
                                </div>
                                <div className="flex-self-end fontWeightNormal fontSize stageExpandButton">
                                    {totalJobCount > 0 &&
                                        <Button
                                            subtle={true}
                                            id={stage.id + "expand"}
                                            iconProps={{ iconName: "ChevronUnfold10" }}
                                            ariaLabel={showExpandedCard ? Resources.CollapseStage : Resources.ExpandStage}
                                            onClick={(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => {
                                                if (event) { event.preventDefault() };
                                                showExpandedCard ? this._hideStageDetails(stage.id) : this._showStageDetails(stage.id);
                                            }
                                            } />
                                    }
                                    {totalJobCount <= 0 &&
                                        <div className="stageExpandButtonPlaceholder" />
                                    }
                                </div>
                            </div>
                            <div className="flex-row secondary-text body-s stage-progress-row">
                                <div className="flex-grow">
                                    {
                                        stageStatusMessage.type === MessageType.Plain && (
                                            <span>{stageStatusMessage.text}</span>
                                        )
                                    }
                                    {
                                        stageStatusMessage.type === MessageType.Primary && (
                                            <PrimaryMessage text={stageStatusMessage.text} />
                                        )
                                    }
                                    {
                                        stageStatusMessage.type === MessageType.Error && (
                                            <span className="error-text">{stageStatusMessage.text}</span>
                                        )
                                    }
                                </div>
                                {
                                    stage.startTime &&
                                    <Duration startDate={stage.startTime} endDate={stage.finishTime} />
                                }
                            </div>
                            {
                                <div className={css("flex-row body-s stage-info-row", metadataCount === 0 && "hidden")}>
                                    <div className="flex-grow">
                                        {cardStatus.metadata}
                                    </div>
                                </div>
                            }
                        </>
                    );

                    const secondaryContent = (
                        <>
                            {jobRows}
                        </>
                    );

                    let stageAriaLabel = format(Resources.StageDescription, stageName, status.label, stageStatusMessage.text);
                    if (stage.startTime) {
                        stageAriaLabel = format(Resources.StageDescriptionWithStartTime, stageName, status.label, stageStatusMessage.text, DateUtil.tooltipString(stage.startTime));
                    }

                    return (
                        <GraphCard
                            id={stage.id}
                            ariaLabel={stageAriaLabel}
                            key={stage.id + "zone"}
                            primaryContent={primaryContent}
                            secondaryContent={showExpandedCard ? secondaryContent : undefined}
                            onActivate={(event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => { onClickFPS(this.context.pageContext, stageUrl, true, event); }}
                            footerButtonProps={footerButtonProps}
                            footerSplitButtonProps={footerSplitButtonProps} />
                    );
                }
            });

            if (stage.dependsOn) {
                stage.dependsOn.forEach((dependency: string) => {
                    if (!edges[dependency]) {
                        edges[dependency] = {};
                    }
                    edges[dependency][stage.id] = true;
                });
            }
        });

        return (
            <div className="stage-graph">
                <GraphList nodes={nodes} edges={edges} horizontalMargin={32} verticalMargin={32} nodeWidth={224} nodeHeight={primaryCardHeight} leftMargin={32} />
            </div>
        );
    }

    public componentDidMount() {
        this._observable.subscribe(this._onObservableChanged);
    }

    public componentWillUnmount() {
        this._observable.unsubscribe(this._onObservableChanged);
    }

    private _getMetadataCount(stage: string) {
        const stageMetaDataValue = this._stageMetadata[stage] || {};
        return ((stageMetaDataValue.hasTests && 1) || 0) + ((stageMetaDataValue.hasArtifacts && 1) || 0) + ((stageMetaDataValue.hasChecks && 1) || 0);
    }

    private _getStageCardStatus(runId: number, stageRefName: string, recordState: TimelineRecordState, recordResult: TaskResult, jobIds: string[], completedJobCount: number, stateData: StageStateData | undefined, checks: CheckReference[], cancelledStages: string[],
        onReviewApprovalsClick: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void,
        onReviewManualValidationClick: (event: React.MouseEvent<HTMLElement> | React.KeyboardEvent<HTMLElement>) => void): IStageCardStatus {
        const jobCount = jobIds.length;
        const message: IStageCardMessage = {
            text: "",
            type: MessageType.Plain
        };
        let showExpandedCard = false;

        const testElement: JSX.Element = <TestStatus
            stageData={{ buildId: runId, stage: stageRefName }}
            onData={(hasData) => this._onTestsData(hasData, stageRefName)}
            recordState={recordState}
        />;
        const artifactsElement: JSX.Element = <ArtifactStatus
            rundata={{ buildId: runId, jobIds: jobIds }}
            onData={(hasData) => this._onArtifactsData(hasData, stageRefName)}
        />;;

        let checkElement: JSX.Element | null = null;

        // check if the stage is getting cancelled but has not been cancelled yet then show the cancelling stage
        if (cancelledStages.length > 0 && cancelledStages.indexOf(stageRefName) >= 0 && recordState !== TimelineRecordState.Completed) {
            message.text = Resources.Cancelling;
            showExpandedCard = true;
        }
        else {
            checks = checks || [];
            const manualValidationsLength =  checks.filter(x => equals(ManualValidationTaskTypeIdString, x.typeId, true)).length;
            const rejectedChecks = checks.filter(x => x.status === CheckRunStatus.Rejected && !equals(ManualValidationTaskTypeIdString, x.typeId, true)).length;
            const rejectedManualValidations = checks.filter(x => equals(ManualValidationTaskTypeIdString, x.typeId, true) && x.status === CheckRunStatus.Rejected).length;
            
            if (checks.length > 0) {
                checkElement = <div>
                    {(checks.length - manualValidationsLength > 0) &&
                        <CheckStatus
                            checks={checks}
                            rejectedChecks={rejectedChecks}
                            showIssues={this._showCheckIssues}
                            onClick={onReviewApprovalsClick}
                            onData={(hasData) => this._onChecksData(hasData, stageRefName)}
                        />}
                    {(manualValidationsLength > 0) &&
                        <ManualValidationStatus
                            checks={checks}
                            rejectedManualValidations={rejectedManualValidations}
                            onClick={onReviewManualValidationClick}
                            onData={(hasData) => this._onChecksData(hasData, stageRefName)}
                        />}
                </div>;
            }

            if (recordState === TimelineRecordState.Pending) {
                message.text = Resources.NotStarted;

                if (stateData?.pendingChecks) {
                    message.type = MessageType.Primary;
                    message.text = Resources.WaitingForApproval;
                }
            }
            else if (recordState === TimelineRecordState.InProgress) {
                message.text = format(Resources.StageInProgress, completedJobCount, jobCount);
                showExpandedCard = true;
            }
            else if (recordState === TimelineRecordState.Completed) {
                if (completedJobCount === 1) {
                    message.text = Resources.SingleJobCompleted;
                }
                else {
                    message.text = format(Resources.MultipleJobsCompleted, completedJobCount);
                }

                switch (recordResult) {
                    case TaskResult.Failed:
                        showExpandedCard = true;
                        if (rejectedChecks > 0) {
                            // For rejected checks no seperate message is shown.CheckStatus message is sufficient.
                            message.text = "";
                        }
                        else {
                            message.text = Resources.Failed;
                        }
                        break;
                    case TaskResult.SucceededWithIssues:
                        showExpandedCard = true;
                        break;
                    case TaskResult.Skipped:
                        message.text = Resources.Skipped;
                        break;
                    case TaskResult.Canceled:
                        message.text = Resources.Canceled;
                        break;
                }
            }
        }

        return {
            message: message,
            expanded: showExpandedCard,
            status: getTimelineStatusIndicatorData(recordState, recordResult, stateData?.pendingChecks, !stateData?.pendingDependencies),
            metadata: <MetaDataElement>
                {testElement}
                {artifactsElement}
                {checkElement}
            </MetaDataElement>
        };
    }

    private _getStageDependencyState = (stage: RunStageData, stages: RunStageData[]): StageDependencyState => {
        const dependencyStageStates = new Map<string, boolean>();

        const getDependencyState = (stage: RunStageData): StageDependencyState => {
            for (const otherStage of stages) {
                if (otherStage.dependsOn && otherStage.dependsOn.indexOf(stage.id) !== -1) {
                    if (dependencyStageStates.has(otherStage.id)) {
                        return dependencyStageStates.get(otherStage.id) ? StageDependencyState.InProgress : StageDependencyState.Completed;
                    }

                    if (otherStage.state === TimelineRecordState.InProgress || getDependencyState(otherStage) === StageDependencyState.InProgress) {
                        dependencyStageStates.set(otherStage.id, true);
                        return StageDependencyState.InProgress;
                    } else {
                        dependencyStageStates.set(otherStage.id, false);
                    }
                }
            }
            return dependencyStageStates.size != 0 ? StageDependencyState.Completed : StageDependencyState.None;
        };
        return getDependencyState(stage);
    }

    private _canRetry = (stage: RunStageData, stages: RunStageData[]) => {

        const stageState = new Map<string, boolean>();

        const anyChildrenRunning = (stage: RunStageData): boolean => {
            for (const otherStage of stages) {

                if (otherStage.dependsOn && otherStage.dependsOn.indexOf(stage.id) != -1) {

                    if(stageState.has(otherStage.id)) {
                        return stageState.get(otherStage.id) as boolean;
                    }

                    if (otherStage.state === TimelineRecordState.InProgress || anyChildrenRunning(otherStage)) {
                        stageState.set(otherStage.id, true);
                        return true;
                    }
                }
            }
            stageState.set(stage.id, false);
            return false;
        }

        // The stage can be retried if it is complete and it's children are not running
        return stage.state === TimelineRecordState.Completed && !anyChildrenRunning(stage);
    }

    private _onArtifactsData = (hasData: boolean, stageRefName: string) => {
        let changed = false;
        const existingStageData = this._stageMetadata[stageRefName];
        if (existingStageData) {
            if (existingStageData.hasArtifacts !== hasData) {
                existingStageData.hasArtifacts = hasData;
                changed = true;
            }
        }
        else {
            this._stageMetadata[stageRefName] = {
                hasArtifacts: hasData,
                hasChecks: false,
                hasTests: false
            };
            changed = true;
        }
        if (changed) {
            this.forceUpdate();
        }
    }

    private _onChecksData = (hasData: boolean, stageRefName: string) => {
        let changed = false;
        const existingStageData = this._stageMetadata[stageRefName];
        if (existingStageData) {
            if (existingStageData.hasChecks !== hasData) {
                existingStageData.hasChecks = hasData;
                changed = true;
            }
        }
        else {
            this._stageMetadata[stageRefName] = {
                hasArtifacts: false,
                hasChecks: hasData,
                hasTests: false
            };
            changed = true;
        }
        if (changed) {
            this.forceUpdate();
        }
    }

    private _onTestsData = (hasData: boolean, stageRefName: string) => {
        let changed = false;
        const existingStageData = this._stageMetadata[stageRefName];
        if (existingStageData) {
            if (existingStageData.hasTests !== hasData) {
                existingStageData.hasTests = hasData;
                changed = true;
            }
        }
        else {
            this._stageMetadata[stageRefName] = {
                hasArtifacts: false,
                hasChecks: false,
                hasTests: hasData
            };
            changed = true;
        }

        if (changed) {
            this.forceUpdate();
        }
    }

    private _cancelStage = (event: React.MouseEvent<HTMLElement>, stage: string): void => {
        event.preventDefault();
        const confirmationText = format(Resources.ConfirmCancelStage, stage);
        this._confirmAction(Resources.CancelStage, confirmationText, () => {
            this._cancelledStages.push(stage);
            this.setState({
                cancelledStages: this._cancelledStages
            });
            this._service.cancelStage(stage);
        });
    }

    private _retryStage = (event: React.MouseEvent<HTMLElement>, stage: string, title: string, confirmationText: string, forceRetryAllJobs: boolean = false, retryDependencies: boolean = true): void => {
        event.preventDefault();
        this._confirmAction(title, confirmationText, () => {
            this._retriedStages.push(stage);
            this.setState({
                retriedStages: this._retriedStages
            });
            this._service.retryStage(stage, forceRetryAllJobs, retryDependencies);
        });
    }

    private _retryStageOld = (event: React.MouseEvent<HTMLElement>, stage: string, retryType: RetryType = RetryType.None, retryDependencies: boolean = true): void => {
        event.preventDefault();
        let confirmationText: string = "";
        if (retryType == RetryType.FailedJobs) {
            confirmationText = format(Resources.ConfirmRerunStageFailedJobs, stage)
        }
        else if (retryDependencies) {
            confirmationText = format(Resources.ConfirmRerunStageAndDependents, stage);
        }
        else {
            confirmationText = format(Resources.ConfirmRerunStage, stage);
        }
        this._confirmAction(Resources.RerunStage, confirmationText, () => {
            this._retriedStages.push(stage);
            this.setState({
                retriedStages: this._retriedStages
            });
            this._service.retryStage(stage, retryType == RetryType.AllJobs, retryDependencies);
        });
    }

    private _onObservableChanged = (data: RunDetailsData) => {
        // Reset cancel and retry button once we have confirmation from the server that those actions have been performed
        this._cancelledStages = this._cancelledStages.filter(x => data.stages.find(y => y.refName === x && y.state === TimelineRecordState.Completed));
        this._retriedStages = this._retriedStages.filter(x => data.stages.find(y => y.refName === x && y.state !== TimelineRecordState.Completed));

        this.setState({
            run: data
        });
    }

    private _showStageDetails = (stageId: string) => {
        const i = this._minimized.indexOf(stageId);
        if (i >= 0) {
            this._minimized.splice(i, 1);
        }

        this._expanded.push(stageId);

        this.setState({
            expanded: this._expanded.slice(),
            minimized: this._minimized.slice()
        });
    }

    private _hideStageDetails = (stageId: string) => {
        const i = this._expanded.indexOf(stageId);
        if (i >= 0) {
            this._expanded.splice(i, 1);
        }

        this._minimized.push(stageId);

        this.setState({
            expanded: this._expanded.slice(),
            minimized: this._minimized.slice()
        });
    }

    private _confirmAction = (title: string, description: string, callback: () => any) => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout((onDismiss) => {
            const props: IConfirmPipelineActionProps = {
                title: title,
                message: description,
                onClose: onDismiss,
                onConfirm: callback
            };

            return React.createElement(CommandComponent, {
                commandType: "ciDConfirmPipelineComponent",
                ...props,
            }, null);
        });
    }

    private _showRetryStageButton(checks: CheckReference[])  {
        checks = checks || [];
        return checks.filter(x => x.status === CheckRunStatus.TimedOut).length > 0;
    }
}

interface IJobRowProps {
    job: RunJobData,
    jobUrl: string,
    status: StageStateData
}

function JobRow(props: IJobRowProps): JSX.Element {
    const jobStatus = getTimelineStatusIndicatorData(props.job.state, props.job.result, props.status.pendingChecks, !props.status.pendingDependencies);

    return (
        <FPSLink className="job-row can-activate-item flex-row fontSize" key={props.job.id} href={props.jobUrl}>
            <Tooltip text={jobStatus.label}>
                <span className="flex-row">
                    <Status {...jobStatus.statusProps} className="icon-large-margin" size={StatusSize.m} />
                </span>
            </Tooltip>
            <div className="flex-row scroll-hidden flex-grow">
                <Tooltip overflowOnly={true} text={props.job.name}>
                    <span className="text-ellipsis">{props.job.name}</span>
                </Tooltip>
            </div>
            {
                props.job.startTime &&
                <Duration className="secondary-text" startDate={props.job.startTime} endDate={props.job.finishTime} />
            }
        </FPSLink>
    );
}

interface IGraphCardProps {
    id: string,
    ariaLabel: string,
    primaryContent: JSX.Element,
    secondaryContent: JSX.Element | undefined,
    footerButtonProps?: IButtonProps[];
    footerSplitButtonProps?: ISplitButtonProps;
    onActivate: (event: React.SyntheticEvent<HTMLElement>) => any
}

function GraphCard(props: IGraphCardProps): JSX.Element {

    const onCardClicked = (event: React.MouseEvent<HTMLElement>) => {
        if (event && !event.defaultPrevented) {
            props.onActivate(event);
        }
    }

    const onCardKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
        if (event && !event.defaultPrevented && event.which === KeyCode.enter) {
            props.onActivate(event);
        }
    }

    return (
        <FocusZone
            key={props.id}
            direction={FocusZoneDirection.Vertical}
            focusGroupProps={{ defaultElementId: props.id }}>
            <div>
                <FocusGroupContext.Consumer>
                    {focusGroupContext => (
                        <FocusZoneContext.Consumer>
                            {zoneContext => (
                                <div
                                    className="stage-card flex-column cursor-pointer"
                                    role="link"
                                    aria-label={props.ariaLabel}
                                    data-focuszone={zoneContext.focuszoneId}
                                    tabIndex={focusGroupContext.focusedElementId === props.id ? 0 : -1}
                                    id={props.id}
                                    onFocus={() => focusGroupContext.onFocus(props.id)}
                                    onClick={onCardClicked}
                                    onKeyPress={onCardKeyPress}>
                                    <div className="stage-card-primary">
                                        {props.primaryContent}
                                    </div>
                                    {
                                        props.secondaryContent &&
                                        <div className="stage-card-secondary">
                                            {props.secondaryContent}
                                        </div>
                                    }
                                    {
                                        props.footerButtonProps &&
                                        <div className="stage-card-footer">
                                            {props.footerButtonProps.map((footerProps, idx) =>
                                                 <Button key={"stage-card-cancel-button-" + props.id + "-" + idx} {...footerProps} />
                                            )}
                                        </div>
                                    }
                                    {
                                        props.footerSplitButtonProps &&
                                        <div className="stage-card-footer">
                                            <SplitButton
                                                buttonProps={ props.footerSplitButtonProps?.buttonProps }
                                                menuButtonProps={ props.footerSplitButtonProps.menuButtonProps }
                                            />
                                        </div>
                                    }
                                </div>
                            )}
                        </FocusZoneContext.Consumer>
                    )}
                </FocusGroupContext.Consumer>
            </div>
        </FocusZone>
    );
}

enum MessageType {
    Plain,
    Primary,
    Error
}

enum RetryType {
    None,
    FailedJobs,
    AllJobs
}

interface IStageCardMessage {
    text: string;
    type: MessageType;
}

interface IStageCardStatus {
    message: IStageCardMessage,
    metadata: JSX.Element | null;
    expanded: boolean,
    status: IStatusIndicatorData
}

interface IMetaDataElementProps {
    children: React.ReactNode;
}

function MetaDataElement(props: IMetaDataElementProps): JSX.Element {
    return <div className="flex-column">
        {props.children}
    </div>;
}